import { createTRPCClient, httpBatchLink, httpSubscriptionLink, splitLink, TRPCClientError } from '@trpc/client'
import { createTRPCContext } from '@trpc/tanstack-react-query'
// Type-only import from the backend: end-to-end typed calls, no server code
// in the bundle (same pattern as the mobile app).
import type { AppRouter } from '../../../src/api/routers'
import { getSessionToken, setSessionToken } from './session'

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>()

export function createApiClient() {
  const batch = httpBatchLink({
    url: '/trpc',
    headers() {
      const token = getSessionToken()
      return token ? { authorization: `Bearer ${token}` } : {}
    },
    async fetch(input, init) {
      const response = await fetch(input, init)
      // A dead session answers every protected call with 401: clear it
      // so the app returns to the sign-in page instead of spinning.
      if (response.status === 401 && getSessionToken()) {
        setSessionToken(null)
      }
      return response
    },
  })
  // the feed's ticket is fetched over the ordinary link, with the session header
  const plain = createTRPCClient<AppRouter>({ links: [batch] })
  return createTRPCClient<AppRouter>({
    links: [
      splitLink({
        condition: (op) => op.type === 'subscription',
        // server-sent events for the activity feed: the browser's EventSource
        // cannot send headers, so each connection carries a short-lived ticket
        true: httpSubscriptionLink({
          url: '/trpc',
          connectionParams: async () => ({ ticket: await plain.activity.ticket.mutate() }),
        }),
        false: batch,
      }),
    ],
  })
}

export function errorMessage(error: unknown, fallback = 'Something went wrong.') {
  if (error instanceof TRPCClientError) return error.message || fallback
  if (error instanceof Error) return error.message || fallback
  return fallback
}
